import { TicketOfficeView } from '../../../types/ticket-office';
import { IAdditionalSeatResponse, ISeatResponse, Seats, TicketType } from '../../../types/types';
import { SelectOption } from '../../components/Select';
import { TicketRow, TicketsTableProps } from '../../components/TicketsTable';
import { removeNumberedSeat, removeUnnumberedSeat, updateTicketType } from './seat-utils';

const getTypeOption = (type: TicketType): SelectOption => ({
    value: String(type.id),
    label: type.name,
});

const getNumberedSeatRow = (
    view: TicketOfficeView,
    seat: ISeatResponse,
    seatTypeId: number,
): TicketRow => {
    const { state } = view;

    const ticketTypeOptions = state.ticketTypes
        .filter((newType) => !seatTypeId || !newType.seatTypeIds.length || newType.seatTypeIds.includes(seatTypeId))
        .map(getTypeOption);

    return {
        key: 'numbered-' + seat.row + '-' + seat.number,
        typeInput: {
            options: ticketTypeOptions,
            value: String(seat.type),
            onChange: async (value): Promise<void> => {
                await updateTicketType(view, seat, Number(value));
            },
        },
        numbered: {
            rowNumber: seat.row,
            seatNumber: seat.number,
            sectionName: seat.section_name,
        },
        onRemove: (): void => void removeNumberedSeat(view, seat.row, seat.number, seat.section_id),
    };
};

const getUnnumberedSeatRow = (
    view: TicketOfficeView,
    seat: IAdditionalSeatResponse,
    typeOptions: SelectOption[],
): TicketRow => {
    const { state } = view;

    return {
        key: 'unnumbered-' + (seat.uuid || seat.tempId),
        typeInput: {
            options: typeOptions,
            value: String(seat.type),
            onChange: async (value): Promise<void> => {
                await updateTicketType(view, seat, Number(value));
            },
            disabled: !seat.uuid,
        },
        unnumberedText: state.lang.tickets.extraSeat,
        onRemove: (): void => void removeUnnumberedSeat(view, seat.uuid),
    };
};

export const getTicketsTableProps = (
    view: TicketOfficeView,
    seats: Seats,
): TicketsTableProps | undefined => {
    const { state } = view;
    const { lang, seatTypes, ticketTypes } = state;

    const ticketRows: TicketRow[] = [];

    const sortedSeats = [...seats.numbered].sort((seat1, seat2) => {
        // First sort by section name (treating undefined/null as empty string)
        const section1 = seat1.section_name || '';
        const section2 = seat2.section_name || '';
        const sectionCompare = section1.localeCompare(section2);
        if (sectionCompare !== 0) {
            return sectionCompare;
        }
        
        // Then sort by row number
        const rowCompare = seat1.row - seat2.row;
        if (rowCompare !== 0) {
            return rowCompare;
        }
        
        // Finally sort by seat number
        return seat1.number - seat2.number;
    });

    for (const seat of sortedSeats) {
        const seatTypeId = seatTypes[seat.row][seat.number];
        ticketRows.push(getNumberedSeatRow(view, seat, seatTypeId));
    }

    const allTicketTypeOptions = ticketTypes.map(getTypeOption);

    for (const seat of seats.unnumbered) {
        ticketRows.push(getUnnumberedSeatRow(view, seat, allTicketTypeOptions));
    }

    if (!ticketRows.length) {
        return;
    }

    return {
        headers: {
            tickets: {
                singular: lang.tickets.singular,
                plural: lang.tickets.plural,
            },
            section: lang.tickets.section,
            row: lang.tickets.row,
            seat: lang.tickets.seat,
            remove: lang.common.remove,
        },
        rows: ticketRows,
    };
};
